<template>
  <div class="utm-analytics-page">
    <div class="header-container">
      <div class="header-heading">
        <h1 class="header-title">
          UTM Analytics
        </h1>
        <p class="header-subtitle">
          Track performance across your UTM sources in real time
        </p>
      </div>
      <div class="auto-refresh-container" title="Auto refresh">
        <v-progress-circular :rotate="-90" :size="44" :width="3" :value="progressValue" color="primary">
          <span class="countdown-text">{{ countdownSeconds }}s</span>
        </v-progress-circular>
      </div>
    </div>

    <v-card class="filter-bar" rounded="lg" flat outlined>
      <div class="filter-group">
        <SelectStorePopover :storeSelectWatcher="onSelectedStores" :stores="stores" />
        <v-select
          :items="utmTypes"
          hide-details="auto"
          background-color="white"
          placeholder="UTM Type"
          outlined
          dense
          class="utm-type-select"
          v-model="utm_type"
          @change="onUtmTypeChange"
        ></v-select>
        <PickDatePopover
          v-if="showExport"
          :handler="pickedDate"
          :timezone="pagePreference.standardTimezone || 'America/Los_Angeles'"
        />
      </div>
      <v-spacer></v-spacer>
      <div class="filter-actions">
        <v-btn v-if="showRegenerate" depressed color="primary" class="rounded-lg text-none" @click="doCalculate">
          <v-icon left size="18">mdi-refresh</v-icon>
          Recalculate
        </v-btn>
        <v-btn depressed color="success" class="rounded-lg text-none" @click="exportExcel">
          <v-icon left size="18">mdi-microsoft-excel</v-icon>
          Export Excel
        </v-btn>
      </div>
    </v-card>

    <v-card class="table-card" rounded="lg" flat outlined>
      <v-data-table
        v-if="getTimezoneDone"
        :headers="headers"
        :items="items1"
        hide-default-footer
        :items-per-page="-1"
        show-expand
        :single-expand="false"
        :expanded.sync="expanded"
        item-key="_rowKey"
        class="utm-table"
      >
        <template v-slot:[`item.store`]="{ item }">
          <div class="store-cell">
            <span class="store-name">{{ item.store && item.store.name }}</span>
            <span class="store-domain">{{ item.store && item.store.domain }}</span>
          </div>
        </template>
        <template v-slot:[`item.revenue`]="{ item }">
          <span class="revenue-cell">{{ formatCurrency(item.revenue) }}</span>
        </template>
        <!-- Expand toggle: show a badge with number of detail rows -->
        <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
          <v-btn
            v-if="item.utmDetails && item.utmDetails.length"
            small
            text
            class="expand-btn text-none"
            @click="expand(!isExpanded)"
          >
            <v-icon size="18" :class="{ 'rotate-180': isExpanded }">mdi-chevron-down</v-icon>
            <span class="expand-count">{{ item.utmDetails.length }}</span>
          </v-btn>
          <span v-else class="no-details">—</span>
        </template>
        <!-- Expanded content: responsive card grid, no horizontal scroll -->
        <template v-slot:expanded-item="{ headers: cols, item }">
          <td :colspan="cols.length" class="expanded-cell">
            <div class="detail-wrap">
              <div class="detail-header-row">
                <span class="detail-title">
                  UTM Details
                  <span class="detail-badge">{{ item.utmDetails.length }}</span>
                </span>
              </div>

              <div v-if="item.utmDetails.length" class="detail-grid">
                <div v-for="(d, i) in item.utmDetails" :key="i" class="detail-card">
                  <div class="detail-card-index">#{{ i + 1 }}</div>
                  <div v-if="detailEntries(d).length" class="detail-fields">
                    <div v-for="f in detailEntries(d)" :key="f.key" class="detail-field">
                      <span class="field-label">{{ f.label }}</span>
                      <span class="field-value">{{ f.value }}</span>
                    </div>
                  </div>
                  <div v-else class="field-value muted">No data</div>
                </div>
              </div>
              <div v-else class="detail-empty">No matching UTM details.</div>
            </div>
          </td>
        </template>
      </v-data-table>
      <v-skeleton-loader v-else type="table"></v-skeleton-loader>
    </v-card>
  </div>
</template>

<script>
import PickDatePopover from '../analytics/components/PickDatePopoverNoCompare';
import SelectStorePopover from '../analytics/components/SelectStorePopover';
import { FETCH_PAGE_PREFERRENCE, FETCH_STORES } from '@/store/actions.type';
import STORAGE_NAME from '@/const/storage';
import moment from 'moment';
import analyticRequest from '@/apis/request/analyticRequest';
import * as XLSX from 'xlsx';
export default {
  components: {
    SelectStorePopover,
    PickDatePopover,
  },
  data: function() {
    let query = this.$route.query;
    return {
      isLoadingReport: false,
      stores: query?.stores?.split(',') || [],
      getTimezoneDone: true,
      search: query.search || '',
      items: [
        {
          key: 'Product has sale',
          value: 'true',
        },
        {
          key: 'Product no sale',
          value: 'false',
        },
        {
          key: 'All products',
          value: 'all',
        },
      ],
      productHasSale: query?.sales || 'true',
      filters: [
        {
          key: 'Total Sales',
          value: 'SHOP',
        },
        {
          key: 'Product',
          value: 'PRODUCT',
        },
        {
          key: 'Variant option',
          value: 'VARIANT',
        },
        {
          key: 'Sale over time',
          value: 'SHOP_SALES_OVERTIME',
        },
        {
          key: 'Variant SKU',
          value: 'VARIANT_SKU',
        },
        {
          key: 'Product vendor',
          value: 'PRODUCT_VENDOR',
        },
        {
          key: 'Product type',
          value: 'PRODUCT_TYPE',
        },
      ],
      filter: query.filter || 'SHOP',
      dateRange: {
        from: null,
        to: null,
      },
      headers_first: [
        {
          text: 'Revenue',
          align: 'start',
          sortable: false,
          value: 'revenue',
        },
        {
          text: 'Basecost',
          align: 'start',
          sortable: false,
          value: 'basecost',
        },
        {
          text: 'Handling',
          align: 'start',
          sortable: false,
          value: 'handling',
        },
        {
          text: 'Dispute',
          align: 'start',
          sortable: false,
          value: 'dispute',
        },
        {
          text: 'Profit',
          align: 'start',
          sortable: false,
          value: 'profit',
        },
        {
          text: 'Orders',
          align: 'start',
          sortable: false,
          value: 'orders',
        },
        {
          text: 'AOV',
          align: 'start',
          sortable: false,
          value: 'aov',
        },
        // {
        //   text: 'CR',
        //   align: 'start',
        //   sortable: false,
        //   value: 'cr',
        // },
      ],
      headers: [
        {
          text: 'Store',
          align: 'start',
          sortable: false,
          value: 'store',
        },
        {
          text: 'UTM Source',
          align: 'start',
          sortable: false,
          value: 'utm_source',
        },
        {
          text: 'Views',
          align: 'start',
          sortable: false,
          value: 'view_content',
        },
        {
          text: 'Add to Cart',
          align: 'start',
          sortable: false,
          value: 'add_to_cart',
        },
        {
          text: 'Reached checkout',
          align: 'start',
          sortable: false,
          value: 'reached_checkout',
        },
        {
          text: 'Orders',
          align: 'start',
          sortable: false,
          value: 'orders',
        },
        {
          text: 'Sale Value',
          align: 'start',
          sortable: false,
          value: 'revenue',
        },
        // {
        //   text: 'CR',
        //   align: 'start',
        //   sortable: false,
        //   value: 'cr',
        // },
      ],
      items0: [
        // {
        //   cr: 1,
        //   aov: 2,
        //   orders: 0,
        //   revenue: 0,
        //   profit: 0,
        //   hold: 0,
        //   basecost: 0,
        //   handling: 0,
        //   dispute: 0,
        // },
      ],
      items1: [
        // {
        //   datetime: 'Total',
        //   order: '95',
        //   revenue: '350',
        //   basecost: '56',
        //   handing: '12',
        //   hold: '0',
        //   dispute: '5',
        //   profit: '277',
        // },
        // {
        //   test: '234',
        //   datetime: '6/19/2023',
        //   order: '30',
        //   revenue: '100',
        //   basecost: '28',
        //   handing: '6',
        //   hold: '0',
        //   dispute: '2.5',
        //   profit: '63.5',
        // },
        // {
        //   datetime: '6/19/2023',
        //   order: '65',
        //   revenue: '250',
        //   basecost: '28',
        //   handing: '6',
        //   hold: '0',
        //   dispute: '2.5',
        //   profit: '213.5',
        // },
      ],
      dates: ['2019-09-10', '2019-09-20'],
      showExport: true,
      showDatePicker: false,
      showRegenerate: false,
      utm_type: 'content',
      utmTypes: [
        {
          text: 'Campaign',
          value: 'campaign',
        },
        {
          text: 'Content',
          value: 'content',
        },
        {
          text: 'Ads Name',
          value: 'adsname',
        },
        {
          text: 'CPID',
          value: 'cpid',
        },
      ],
      autoRefreshInterval: null,
      countdownInterval: null,
      countdownSeconds: 30,
      autoRefreshDuration: 30,
      expanded: [],
      // Fields never shown/exported even when present in utmDetails
      hiddenDetailKeys: ['fbclid'],
      // Nice labels for known keys; anything else is auto-formatted from its key
      detailLabelMap: {
        source: 'Source',
        medium: 'Medium',
        content: 'Content',
        campaign: 'Campaign',
        adname: 'Ad Name',
        cpid: 'CPID',
        utm_campaign_id: 'UTM Campaign ID',
        utm_adset: 'UTM Adset',
        utm_adset_id: 'UTM Adset ID',
        utm_ad_id: 'UTM Ad ID',
        utm_placement: 'UTM Placement',
        fb_campaign: 'FB Campaign',
        fb_campaignid: 'FB Campaign ID',
        fb_adset: 'FB Adset',
        fb_adsetid: 'FB Adset ID',
        fb_adid: 'FB Ad ID',
      },
    };
  },
  async beforeMount() {
    if (!this.pagePreference.standardTimezone) {
      this.getTimezoneDone = false;
      await this.$store.dispatch(FETCH_PAGE_PREFERRENCE);
      this.getTimezoneDone = true;
    }
  },
  async created() {
    await this.$store.dispatch(FETCH_STORES);
    await this.getUtmAnalytic();
    this.startAutoRefresh();
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '$0.00';
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(numValue) ? '$0.00' : `$${numValue.toFixed(2)}`;
    },
    async getUtmAnalytic() {
      let fromTime = this.dateRange.from
        ? moment(this.dateRange.from).format('YYYY-MM-DD')
        : moment()
            .tz(this.pagePreference.standardTimezone || 'America/Los_Angeles')
            .format('YYYY-MM-DD');
      let toTime = this.dateRange.to
        ? moment(this.dateRange.to).format('YYYY-MM-DD')
        : moment()
            .tz(this.timezone || 'America/Los_Angeles')
            .format('YYYY-MM-DD');
      let _stores = this.$route.stores ? this.$route.stores : this.stores ? this.stores : '';
      const params = {
        from_time: fromTime,
        to_time: toTime,
        stores: Array.isArray(_stores) ? _stores?.join(',') : _stores,
        timezone: this.pagePreference.standardTimezone || 'America/Los_Angeles',
        utm_type: this.utm_type,
      };
      if (!params.utm_type) delete params.utm_type;
      let data = await analyticRequest.get(`/actions/utm_analytics_dropnoxe`, {
        params,
      });

      if (data?.data?.firstRow) this.items0 = [data?.data?.firstRow];

      if (data?.data?.results) {
        // Attach a stable key per row so expand/collapse state survives refreshes
        this.items1 = data.data.results.map((row, index) => ({
          ...row,
          utmDetails: Array.isArray(row.utmDetails) ? row.utmDetails : [],
          _rowKey: this.buildRowKey(row, index),
        }));
      }
    },
    buildRowKey(row, index) {
      const store = row.store || {};
      return [store.domain || store.name || '', row.utm_source || '', index].join('|');
    },
    formatDetailLabel(key) {
      if (this.detailLabelMap[key]) return this.detailLabelMap[key];
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    // Non-empty, non-hidden fields actually present in a single utmDetails entry
    detailEntries(d) {
      if (!d) return [];
      return Object.keys(d)
        .filter(key => !this.hiddenDetailKeys.includes(key))
        .map(key => ({ key, label: this.formatDetailLabel(key), value: d[key] }))
        .filter(f => f.value !== null && f.value !== undefined && String(f.value).trim() !== '');
    },
    // Union of field keys (in first-seen order) across all details of a row, minus hidden/all-empty
    detailKeysFor(details) {
      const keys = [];
      const seen = new Set();
      (details || []).forEach(d => {
        Object.keys(d || {}).forEach(key => {
          if (this.hiddenDetailKeys.includes(key)) return;
          const hasValue = (details || []).some(
            row => row && row[key] !== null && row[key] !== undefined && String(row[key]).trim() !== '',
          );
          if (hasValue && !seen.has(key)) {
            seen.add(key);
            keys.push(key);
          }
        });
      });
      return keys;
    },

    onSelectedStores(stores) {
      this.stores = stores;
      this.getUtmAnalytic();
      this.startAutoRefresh();
    },
    onUtmTypeChange() {
      this.getUtmAnalytic();
      this.startAutoRefresh();
    },
    pickedDate(dates) {
      this.dateRange = {
        from: dates.startDate,
        to: dates.endDate,
      };
      this.getUtmAnalytic();
      this.startAutoRefresh();
    },
    async doCalculate() {
      let data = await analyticRequest.post(`/actions/regenate_profit`, {});
    },
    async exportCsv() {
      try {
        this.isLoadingReport = true;
        let query = this.$route.query;
        const url = 'products/sale-report/export-excel';

        let data = await analyticRequest.get(url, { params: query });
        let text = data.data;
        console.log(text);
        var element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=UTF-8,' + encodeURIComponent(text));
        element.setAttribute('download', 'sale-report.csv');
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      } catch (error) {
        console.log(error);
      }
      this.isLoadingReport = false;
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.countdownSeconds = this.autoRefreshDuration;

      this.countdownInterval = setInterval(() => {
        this.countdownSeconds--;
        if (this.countdownSeconds <= 0) {
          this.countdownSeconds = this.autoRefreshDuration;
        }
      }, 1000);

      this.autoRefreshInterval = setInterval(() => {
        this.getUtmAnalytic();
      }, this.autoRefreshDuration * 1000);
    },
    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    exportExcel() {
      const data = this.items1;
      const headers = this.headers;
      if (!data || !data.length) {
        this.$toast && this.$toast.error('Không có dữ liệu để xuất');
        return;
      }
      const summaryCols = headers.filter(h => h.value !== 'data-table-expand');
      const emptySummary = () => {
        const obj = {};
        summaryCols.forEach(h => {
          obj[h.text] = '';
        });
        return obj;
      };
      const buildSummary = row => {
        const obj = {};
        summaryCols.forEach(h => {
          if (h.value === 'store') {
            const store = row.store || {};
            obj[h.text] = [store.name, store.domain].filter(Boolean).join(' - ');
          } else {
            obj[h.text] = row[h.value];
          }
        });
        return obj;
      };
      // Detail columns are derived dynamically from whatever keys the data contains
      const allDetails = data.reduce((acc, row) => acc.concat(row.utmDetails || []), []);
      const detailKeys = this.detailKeysFor(allDetails);
      const buildDetail = d => {
        const obj = {};
        detailKeys.forEach(key => {
          obj[this.formatDetailLabel(key)] = d && d[key] != null ? d[key] : '';
        });
        return obj;
      };

      // One flattened sheet: each detail row sits under its summary row (mirrors the UI)
      const exportData = [];
      data.forEach(row => {
        const summary = buildSummary(row);
        const details = row.utmDetails || [];
        if (!details.length) {
          exportData.push({ ...summary, ...buildDetail(null) });
          return;
        }
        details.forEach((d, i) => {
          exportData.push({
            ...(i === 0 ? summary : emptySummary()),
            ...buildDetail(d),
          });
        });
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'UTM Analytics');
      XLSX.writeFile(wb, 'utm-analytics.xlsx');
    },
  },
  computed: {
    allStores() {
      return this.$store.getters.stores;
    },
    pagePreference() {
      return this.$store.getters.pagePreference;
    },
    dateRangeText() {
      return this.dates.join(' ~ ');
    },
    progressValue() {
      return (this.countdownSeconds / this.autoRefreshDuration) * 100;
    },
  },
  watch: {
    filter: function(val) {},
    productHasSale: function(val) {
      let query = this.$route.query;
      this.$router.push({
        query: {
          size: query?.size,
          page: query?.page,
          stores: query?.stores,
          search: query?.search,
          from_time: query?.from_time,
          to_time: query?.to_time,
          tz: query?.tz,
          sales: val,
        },
      });
    },
    search: async function(val) {
      let query = this.$route.query;
      this.$router.push({
        query: {
          size: this.get?.size,
          page: query?.page,
          stores: query?.stores,
          search: val,
          from_time: query?.from_time,
          to_time: query?.to_time,
          tz: query?.tz,
          sales: query?.sales,
        },
      });
    },
    stores: function(val) {
      let query = this.$route.query;
      this.$router.push({
        query: {
          size: this.get?.size,
          page: query?.page,
          stores: val?.join(','),
          search: query?.search,
          from_time: query?.from_time,
          to_time: query?.to_time,
          tz: query?.tz,
          sales: query?.sales,
        },
      });
    },
    dateRange: function(val) {
      let query = this.$route.query;
      this.$router.push({
        query: {
          size: this.get?.size,
          page: query?.page,
          stores: query?.stores,
          search: query?.search,
          from_time: moment(this.dateRange.from).format('YYYY-MM-DD'),
          to_time: moment(this.dateRange.to).format('YYYY-MM-DD'),
          tz: query?.tz,
          sales: query?.sales,
        },
      });
    },
  },
  destroyed() {
    this.stopAutoRefresh();
    localStorage.removeItem(STORAGE_NAME.SALE_REPORT_QUERY);
  },
};
</script>

<style lang="scss" scoped>
.website-page .website-container {
  max-width: unset !important;
}

.analytic-sale-report {
  text-align: center;
}

.select-filter {
  max-width: 200px;
  min-width: 200px;
}

.analytic-container .l-chart-container {
  .text-black {
    color: #000000;
  }

  .text-bold {
    font-weight: 700;
  }

  .text-small {
    font-size: 12px;
  }

  .l-chart-title {
    font-size: 16px;
  }

  .sub-chart-container {
    height: 100%;
    padding: 16px;
  }

  .sub-chart-container {
    // transform: scale(1.01);

    // margin: -8px;
    .on-hover {
      transition: all 0.5s;
    }
  }
}

.analytic-loading {
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.utm-analytics-page {
  padding: 4px;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  .header-heading {
    flex: 1;
    min-width: 0;
  }

  .header-title {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
    color: #1a1a2e;
  }

  .header-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #8a8a9a;
  }
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #ffffff;
  border-color: #ececf1 !important;

  .filter-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .filter-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .utm-type-select {
    max-width: 200px;
    min-width: 180px;

    ::v-deep .v-input__slot {
      min-height: 36px !important;
      height: 36px;
    }

    ::v-deep .v-input__prepend-inner {
      margin-top: 7px !important;
    }

    ::v-deep .v-select__selections {
      min-height: 36px !important;
    }

    ::v-deep .v-input__append-inner {
      margin-top: 6px !important;
    }
  }

  // Keep every filter control the same height
  ::v-deep .v-btn:not(.v-btn--icon) {
    height: 36px !important;
  }
}

.table-card {
  border-color: #ececf1 !important;
  overflow: hidden;
}

.utm-table {
  ::v-deep thead th {
    background: #f7f8fb;
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6b6b7b !important;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #f5f7ff !important;
    }

    td {
      font-size: 14px;
      color: #2d2d3a;
    }
  }

  .revenue-cell {
    font-weight: 600;
    color: #1976d2;
  }

  .store-cell {
    display: flex;
    flex-direction: column;
    line-height: 1.3;

    .store-name {
      font-weight: 600;
      color: #2d2d3a;
    }

    .store-domain {
      font-size: 12px;
      color: #8a8a9a;
    }
  }

  .expand-btn {
    min-width: 0 !important;
    padding: 0 6px !important;

    .v-icon {
      transition: transform 0.2s ease;
    }

    .rotate-180 {
      transform: rotate(180deg);
    }

    .expand-count {
      margin-left: 2px;
      font-size: 12px;
      font-weight: 700;
      color: #1976d2;
    }
  }

  .no-details {
    color: #c4c4d0;
  }
}

.expanded-cell {
  padding: 0 !important;
  background: #fafbfe;
  box-shadow: inset 0 3px 6px -6px rgba(0, 0, 0, 0.25), inset 0 -3px 6px -6px rgba(0, 0, 0, 0.25);
}

.detail-wrap {
  padding: 14px 20px 18px;
}

.detail-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;

  .detail-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6b6b7b;
  }

  .detail-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #e8effd;
    color: #1976d2;
    font-size: 12px;
    font-weight: 700;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.detail-card {
  position: relative;
  border: 1px solid #ececf1;
  border-radius: 10px;
  background: #ffffff;
  padding: 14px 14px 12px;

  .detail-card-index {
    position: absolute;
    top: 10px;
    right: 12px;
    font-size: 11px;
    font-weight: 700;
    color: #c4c4d0;
  }
}

.detail-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 14px;
}

.detail-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.field-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9a9aa8;
  margin-bottom: 2px;
}

.field-value {
  font-size: 13px;
  line-height: 1.4;
  color: #2d2d3a;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  &.muted {
    color: #c4c4d0;
  }
}

.detail-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: #9a9aa8;
}

.auto-refresh-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .countdown-text {
    font-size: 13px;
    font-weight: 700;
    color: #1976d2;
  }
}

.v-text-field {
  margin-top: 0px !important;
}

@media (max-width: 700px) {
  .filter-bar {
    .filter-group,
    .filter-actions {
      width: 100%;
    }

    .filter-group > *,
    .filter-actions > * {
      flex: 1 1 auto;
    }

    .utm-type-select {
      max-width: 100%;
    }
  }
}
</style>
